import { TILE_SIZE, TILES_H, TILES_W } from "./map-world-dims.js";
import type { LayerAAccentPlacement } from "@cold-case/shared/case-manifest";

const GROUND_SUBCELLS = 16;

/** WebGL max texture dimension on many GPUs; ground bakes are split horizontally. */
export const GROUND_TEXTURE_MAX_PX = 4096;

/** Fade from sidewalk into void beyond the corridor edge (tile units). */
const OUTER_FEATHER_TILES = 9;
/** Soft edge on building footprint void cuts (tile units). */
const FOOTPRINT_FEATHER_TILES = 0.85;

export type FootprintRect = { x0: number; y0: number; x1: number; y1: number };

function lerpChannel(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t);
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0 || 1)));
  return t * t * (3 - 2 * t);
}

function lerpColor(c0: number, c1: number, t: number): number {
  const u = Math.max(0, Math.min(1, t));
  const r0 = (c0 >> 16) & 0xff;
  const g0 = (c0 >> 8) & 0xff;
  const b0 = c0 & 0xff;
  const r1 = (c1 >> 16) & 0xff;
  const g1 = (c1 >> 8) & 0xff;
  const b1 = c1 & 0xff;
  return (
    (lerpChannel(r0, r1, u) << 16) |
    (lerpChannel(g0, g1, u) << 8) |
    lerpChannel(b0, b1, u)
  );
}

function colorToCss(c: number): string {
  const hex = (c & 0xffffff).toString(16).padStart(6, "0");
  return `#${hex}`;
}

/**
 * Corridor ground: asphalt near spine, then sidewalk band to `half`.
 * Buildings are placed outside this radius; this is appearance only.
 */
function walkFillForDist(d: number, half: number, downtown: boolean): number {
  const asphalt = downtown ? 0x161224 : 0x1a261c;
  const asphaltHi = downtown ? 0x251c3a : 0x263327;
  const sidewalk = downtown ? 0x3e3f5c : 0x3d4f41;
  const sidewalkEdge = downtown ? 0x2d2e47 : 0x303e33;
  if (d <= 1.2) {
    return lerpColor(asphaltHi, asphalt, d / 1.2);
  }
  if (d <= half + 0.01) {
    const band = half - 1.2;
    if (band < 1e-6) return sidewalk;
    const u = Math.min(1, Math.max(0, (d - 1.2) / band));
    return lerpColor(sidewalk, sidewalkEdge, u);
  }
  return sidewalk;
}

function voidFillAtWorldY(py: number, hPx: number, downtown: boolean): number {
  const deep = downtown ? 0x090514 : 0x080f0a;
  const lift = downtown ? 0x140f28 : 0x122416;
  const t = hPx > 1 ? py / hPx : 0;
  return lerpColor(deep, lift, t * 0.55 + 0.12);
}

function signedDistToRect(
  fx: number,
  fy: number,
  x0: number,
  y0: number,
  x1: number,
  y1: number
): number {
  const dx = Math.max(x0 - fx, fx - (x1 - 1e-6), 0);
  const dy = Math.max(y0 - fy, fy - (y1 - 1e-6), 0);
  if (dx === 0 && dy === 0) {
    const inLeft = fx - x0;
    const inRight = x1 - fx;
    const inTop = fy - y0;
    const inBottom = y1 - fy;
    return -Math.min(inLeft, inRight, inTop, inBottom);
  }
  return Math.hypot(dx, dy);
}

function footprintVoidStrength(fx: number, fy: number, footprints: FootprintRect[]): number {
  let best = 0;
  for (const b of footprints) {
    const sd = signedDistToRect(fx, fy, b.x0, b.y0, b.x1, b.y1);
    if (sd <= -FOOTPRINT_FEATHER_TILES) {
      best = Math.max(best, 1);
    } else if (sd < 0) {
      best = Math.max(best, smoothstep(-FOOTPRINT_FEATHER_TILES, 0, sd));
    }
  }
  return best;
}

function groundColorAtSubcell(
  distToSpine: (fx: number, fy: number) => number,
  fx: number,
  fy: number,
  half: number,
  downtown: boolean,
  hPx: number,
  footprints: FootprintRect[]
): number {
  const py = fy * TILE_SIZE;
  const deep = voidFillAtWorldY(py, hPx, downtown);
  // Footprint void cuts show through building art padding; façades cover the lot visually.
  const voidPunch = 0;

  const spineD = distToSpine(fx, fy);
  let corridorFill: number;
  if (spineD <= half) {
    corridorFill = walkFillForDist(spineD, half, downtown);
  } else {
    const beyond = spineD - half;
    if (beyond >= OUTER_FEATHER_TILES) {
      corridorFill = deep;
    } else {
      const edgeWalk = walkFillForDist(half, half, downtown);
      const u = smoothstep(0, OUTER_FEATHER_TILES, beyond);
      corridorFill = lerpColor(edgeWalk, deep, u);
    }
  }

  if (voidPunch <= 0) return corridorFill;
  const voidColor = voidFillAtWorldY(py, hPx, downtown);
  return lerpColor(corridorFill, voidColor, voidPunch);
}

/** Tile-based samples per zone bake (regression guard vs per-pixel loops). */
export function groundBakeTileSampleCount(): number {
  return TILES_W * TILES_H;
}

/** CPU-only bake timing hook (no Phaser). */
export function benchmarkGroundBakeSamples(
  distToSpine: (fx: number, fy: number) => number,
  half: number,
  downtown: boolean,
  wPx: number,
  hPx: number,
  footprints: FootprintRect[]
): number {
  let samples = 0;
  for (let ty = 0; ty < TILES_H; ty++) {
    const fy = ty + 0.5;
    for (let tx = 0; tx < TILES_W; tx++) {
      groundColorAtSubcell(distToSpine, tx + 0.5, fy, half, downtown, hPx, footprints);
      samples++;
    }
  }
  return samples;
}

/** Horizontal tile slice for one WebGL-safe ground chunk. */
export function bakeGroundCanvasRegion(
  distToSpine: (fx: number, fy: number) => number,
  half: number,
  downtown: boolean,
  footprints: FootprintRect[],
  txMin: number,
  txMax: number,
  accents?: LayerAAccentPlacement[]
): HTMLCanvasElement {
  const chunkTilesW = txMax - txMin;
  const wPx = chunkTilesW * TILE_SIZE;
  const hPx = TILES_H * TILE_SIZE;
  const canvas = document.createElement("canvas");
  canvas.width = wPx;
  canvas.height = hPx;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("map-ground-bake: 2d context unavailable");
  }

  const sub = GROUND_SUBCELLS;
  const subPx = TILE_SIZE / sub;
  for (let ty = 0; ty < TILES_H; ty++) {
    for (let tx = txMin; tx < txMax; tx++) {
      for (let sy = 0; sy < sub; sy++) {
        for (let sx = 0; sx < sub; sx++) {
          const fx = tx + (sx + 0.5) / sub;
          const fy = ty + (sy + 0.5) / sub;
          const c = groundColorAtSubcell(
            distToSpine,
            fx,
            fy,
            half,
            downtown,
            hPx,
            footprints
          );
          ctx.fillStyle = colorToCss(c);
          ctx.fillRect(
            (tx - txMin) * TILE_SIZE + sx * subPx,
            ty * TILE_SIZE + sy * subPx,
            subPx + 0.5,
            subPx + 0.5
          );
        }
      }
    }
  }

  // Overlay custom ground bakes for the server-defined accents
  for (const acc of accents ?? []) {
    if (acc.tile_x < txMin || acc.tile_x >= txMax) continue;

    const cx = (acc.tile_x - txMin) * TILE_SIZE + TILE_SIZE / 2;
    const cy = acc.tile_y * TILE_SIZE + TILE_SIZE / 2;

    ctx.save();
    ctx.shadowBlur = 0; // reset
    switch (acc.type) {
      case "neon_drain": {
        const color = acc.color ?? "#00f0ff";
        ctx.shadowBlur = 12;
        ctx.shadowColor = color;
        // outer ring
        ctx.strokeStyle = color;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(cx, cy, 18, 0, Math.PI * 2);
        ctx.stroke();
        // inner grate rings/slots
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(cx, cy, 10, 0, Math.PI * 2);
        ctx.stroke();
        
        // cross hairs inside grate
        ctx.beginPath();
        ctx.moveTo(cx - 14, cy); ctx.lineTo(cx + 14, cy);
        ctx.moveTo(cx, cy - 14); ctx.lineTo(cx, cy + 14);
        ctx.stroke();
        break;
      }
      case "neon_grid": {
        const color = acc.color ?? "#ff007f";
        ctx.shadowBlur = 10;
        ctx.shadowColor = color;
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        const size = 16;
        ctx.beginPath();
        // Top-left corner
        ctx.moveTo(cx - size, cy - size + 6);
        ctx.lineTo(cx - size, cy - size);
        ctx.lineTo(cx - size + 6, cy - size);
        
        // Top-right corner
        ctx.moveTo(cx + size, cy - size + 6);
        ctx.lineTo(cx + size, cy - size);
        ctx.lineTo(cx + size - 6, cy - size);
        
        // Bottom-left corner
        ctx.moveTo(cx - size, cy + size - 6);
        ctx.lineTo(cx - size, cy + size);
        ctx.lineTo(cx - size + 6, cy + size);
        
        // Bottom-right corner
        ctx.moveTo(cx + size, cy + size - 6);
        ctx.lineTo(cx + size, cy + size);
        ctx.lineTo(cx + size - 6, cy + size);
        ctx.stroke();
        
        // Draw a small neon center dot
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(cx, cy, 2.5, 0, Math.PI * 2);
        ctx.fill();
        break;
      }
      case "steam_vent": {
        ctx.fillStyle = "#1e1b24";
        ctx.beginPath();
        ctx.arc(cx, cy, 20, 0, Math.PI * 2);
        ctx.fill();
        
        ctx.strokeStyle = "#4d4a57";
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(cx, cy, 20, 0, Math.PI * 2);
        ctx.stroke();
        
        ctx.strokeStyle = "#383540";
        ctx.lineWidth = 2;
        ctx.beginPath();
        for (let offset = -14; offset <= 14; offset += 5) {
          if (offset === 0) continue;
          const len = Math.sqrt(20 * 20 - offset * offset);
          ctx.moveTo(cx + offset, cy - len + 2);
          ctx.lineTo(cx + offset, cy + len - 2);
        }
        ctx.stroke();
        break;
      }
      case "flower_patch": {
        ctx.fillStyle = "#275e3a";
        ctx.beginPath();
        ctx.arc(cx - 6, cy + 4, 8, 0, Math.PI * 2);
        ctx.arc(cx + 8, cy - 4, 9, 0, Math.PI * 2);
        ctx.arc(cx + 2, cy + 8, 7, 0, Math.PI * 2);
        ctx.fill();
        
        ctx.fillStyle = "#378252";
        ctx.beginPath();
        ctx.arc(cx - 2, cy - 6, 8, 0, Math.PI * 2);
        ctx.arc(cx + 6, cy + 4, 7, 0, Math.PI * 2);
        ctx.fill();

        const color = acc.color ?? "#ff79c6";
        ctx.fillStyle = color;
        
        ctx.beginPath();
        ctx.arc(cx - 4, cy - 2, 3.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#ffffff";
        ctx.beginPath();
        ctx.arc(cx - 4, cy - 2, 1.2, 0, Math.PI * 2);
        ctx.fill();
        
        ctx.fillStyle = "#ffb86c";
        ctx.beginPath();
        ctx.arc(cx + 6, cy + 3, 3, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#ffffff";
        ctx.beginPath();
        ctx.arc(cx + 6, cy + 3, 1, 0, Math.PI * 2);
        ctx.fill();
        break;
      }
      case "mystic_sparkle": {
        const color = acc.color ?? "#ffd700";
        ctx.shadowBlur = 8;
        ctx.shadowColor = color;
        ctx.strokeStyle = color;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(cx, cy - 10);
        ctx.lineTo(cx + 7, cy);
        ctx.lineTo(cx, cy + 10);
        ctx.lineTo(cx - 7, cy);
        ctx.closePath();
        ctx.stroke();
        
        ctx.fillStyle = "#ffffff";
        ctx.beginPath();
        ctx.arc(cx, cy, 2, 0, Math.PI * 2);
        ctx.fill();
        break;
      }
      case "stone_path": {
        ctx.fillStyle = "#4a534c";
        ctx.strokeStyle = "#2f3631";
        ctx.lineWidth = 2.5;
        
        ctx.beginPath();
        ctx.ellipse(cx - 6, cy - 6, 12, 8, Math.PI / 6, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        ctx.beginPath();
        ctx.ellipse(cx + 8, cy + 6, 10, 7, -Math.PI / 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        ctx.fillStyle = "#5c665e";
        ctx.beginPath();
        ctx.ellipse(cx + 6, cy - 8, 9, 6, Math.PI / 3, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        break;
      }
    }
    ctx.restore();
  }

  return canvas;
}

export function groundChunkTileSpans(maxChunkPx = GROUND_TEXTURE_MAX_PX): Array<{
  txMin: number;
  txMax: number;
}> {
  const maxTiles = Math.max(1, Math.floor(maxChunkPx / TILE_SIZE));
  const spans: Array<{ txMin: number; txMax: number }> = [];
  for (let tx0 = 0; tx0 < TILES_W; tx0 += maxTiles) {
    spans.push({ txMin: tx0, txMax: Math.min(TILES_W, tx0 + maxTiles) });
  }
  return spans;
}

export function bakeGroundCanvas(
  distToSpine: (fx: number, fy: number) => number,
  half: number,
  downtown: boolean,
  wPx: number,
  hPx: number,
  footprints: FootprintRect[]
): HTMLCanvasElement {
  return bakeGroundCanvasRegion(distToSpine, half, downtown, footprints, 0, TILES_W);
}

/** Survives Phaser scene restarts so zone transit and rejoin skip CPU bake. */
export const groundTextureCache = new Map<string, HTMLCanvasElement>();
